/**
 * 这里的实现基本和state.ts一致。
 * 但是选择商品和选择同货号商品的状态需要隔离。这里复制一份逻辑。
 * 如果有需要修改此文件的逻辑， 需要同步修改state.ts。反之依然。
 */
import { atom, useAtom, useAtomValue, useSetAtom } from 'jotai';
import { produce } from 'immer';
import { useCallback } from 'react';
import { ItemIdMap as ItemIdMap1 } from './state';

/***************** 基础状态 start ******************/
export const QueryParams = atom<{ pageNo: number; pageSize: number; count: number }>({
  pageNo: 1,
  pageSize: 10,
  count: 0,
});

export const useSetCount = () => {
  const setQueryParams = useSetAtom(QueryParams);
  return useCallback(
    (count) => {
      setQueryParams(
        produce((draft) => {
          draft.count = count;
        }),
      );
    },
    [setQueryParams],
  );
};

export const ItemIdMap = atom<Record<string, ItemSelectV2.Item>>({});

export const CurrentItems = atom<ItemSelectV2.Item[]>((get) => {
  const pageInfo = get(QueryParams);
  const itemIdMap = get(ItemIdMap);

  return Object.values(itemIdMap)
    .filter(Boolean)
    .slice((pageInfo.pageNo - 1) * pageInfo.pageSize, pageInfo.pageNo * pageInfo.pageSize);
});

export const useCurrentItems = () => {
  return useAtomValue(CurrentItems);
};

export const SelectedItems = atom<ItemSelectV2.Item[]>((get) => {
  const itemIdMap = get(ItemIdMap);
  return Object.values(itemIdMap).filter(
    (item) => item._checked || item.skuInfo.some((sku) => sku._checked),
  );
});

export const useSelectedItems = () => {
  return useAtomValue(SelectedItems);
};

export const CurrentPageCheckboxState = atom<'checked' | 'indeterminate' | 'unchecked'>((get) => {
  const items = get(CurrentItems);

  const totalCount = items.length;
  const checkedCount = items.filter((item) => item._checked).length;
  const indeterminateCount = items.filter((item) => item._indeterminate).length;

  if (totalCount === 0) {
    return 'unchecked';
  } else if (checkedCount === totalCount && indeterminateCount === 0) {
    return 'checked';
  } else if (checkedCount === 0) {
    return 'unchecked';
  } else {
    return 'indeterminate';
  }
});

export const AllCheckboxState = atom<'checked' | 'indeterminate' | 'unchecked'>((get) => {
  const idMap = get(ItemIdMap);
  const items = Object.values(idMap).filter(Boolean);

  const totalCount = items.length;
  const checkedCount = items.filter((item) => item._checked).length;
  const indeterminateCount = items.filter((item) => item._indeterminate).length;

  if (totalCount === 0) {
    return 'unchecked';
  } else if (checkedCount === totalCount && indeterminateCount === 0) {
    return 'checked';
  } else if (checkedCount === 0) {
    return 'unchecked';
  } else {
    return 'indeterminate';
  }
});

export const useCurrentPageCheckboxState = () => {
  return useAtomValue(CurrentPageCheckboxState);
};

export const useAllCheckboxState = () => {
  return useAtomValue(AllCheckboxState);
};
/***************** 基础状态 end ********************/

/************** 更改状态的方法 start ****************/
// 分页
export function usePage() {
  const [state, setstate] = useAtom(QueryParams);
  const change = useCallback(
    (pageNo: number) => {
      setstate(
        produce((draft) => {
          draft.pageNo = pageNo;
        }),
      );
    },
    [setstate],
  );
  return [state, change] as const;
}

// 修改本页全选状态
export const useToggleCurrentPageChecked = (sellerSku: string) => {
  const items = useAtomValue(CurrentItems);
  const setItemIdMap = useSetAtom(ItemIdMap);
  const itemsIds = items.map((item) => item.externalItemId);

  return useCallback(
    (checked: boolean) => {
      setItemIdMap(
        produce((draft) => {
          itemsIds.forEach((itemId) => {
            const item = draft[itemId];
            for (let i = 0; i < item.skuInfo.length; i++) {
              if (item.skuInfo[i].sellerSku.toLowerCase() === sellerSku.toLowerCase()) {
                item.skuInfo[i]._checked = checked;
                break;
              }
            }
            item._checked = checked;
            item._indeterminate = false;
          });
        }),
      );
    },
    [setItemIdMap, itemsIds, sellerSku],
  );
};

// 修改全选状态
export const useToggleAllChecked = (sellerSku: string) => {
  const setItemIdMap = useSetAtom(ItemIdMap);

  return useCallback(
    (checked: boolean) => {
      setItemIdMap(
        produce((draft) => {
          Object.values(draft).forEach((item) => {
            for (let i = 0; i < item.skuInfo.length; i++) {
              if (item.skuInfo[i].sellerSku.toLowerCase() === sellerSku.toLowerCase()) {
                item.skuInfo[i]._checked = checked;
                break;
              }
            }
            item._checked = checked;
            item._indeterminate = false;
          });
        }),
      );
    },
    [setItemIdMap, sellerSku],
  );
};

export const useClearAll = () => {
  const setItemIdMap = useSetAtom(ItemIdMap);
  const setParams = useSetAtom(QueryParams);

  return useCallback(() => {
    setItemIdMap({});
    setParams({
      pageNo: 1,
      pageSize: 10,
      count: 0,
    });
  }, [setParams, setItemIdMap]);
};

// 同步选中的状态到商品选择器
export const useSyncSelectedItems = () => {
  const selectItems = useSelectedItems();
  const setIdMap = useSetAtom(ItemIdMap1);

  const sync = useCallback(
    (sellerSku: string) => {
      setIdMap(
        produce((draft) => {
          selectItems.forEach((item) => {
            const existItem = draft[item.externalItemId];
            if (existItem) {
              existItem.skuInfo.forEach((existSku) => {
                if (sellerSku?.toLowerCase() === existSku.sellerSku?.toLowerCase()) {
                  existSku._checked = true;
                }
              });
              const checkedSkuCount = existItem.skuInfo.filter((sku) => sku._checked).length;
              if (checkedSkuCount === existItem.skuInfo.length) {
                existItem._indeterminate = false;
                existItem._checked = true;
              } else if (checkedSkuCount === 0) {
                existItem._indeterminate = false;
                existItem._checked = false;
              } else {
                existItem._indeterminate = true;
                existItem._checked = false;
              }
            } else {
              draft[item.externalItemId] = item;
            }
          });
        }),
      );
    },
    [selectItems, setIdMap],
  );

  return [sync, selectItems] as const;
};
/*************** 更改状态的方法 end *****************/
